import React, { useState, useEffect, useContext } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import SalaryTable from "./Salary/SalaryTable";
import SalaryDetail from "./Salary/SalaryDetail/SalaryDetail";
import {
  getSalaryApprovalsByApproverV2,
  rebuildSalaryApprovalPortalCache,
} from "../../api/timesheetApi";
import { getAllUsers } from "../../api/api";
import "./FinancialReport.css";
import { UserContext } from "../../contexts/CurrentUserContext";

registerLocale("ru", ru);
setDefaultLocale("ru");

function Salary() {
  const { userData } = useContext(UserContext);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [paymentsDateFrom, setPaymentsDateFrom] = useState(null);
  const [paymentsDateTo, setPaymentsDateTo] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rebuildLoading, setRebuildLoading] = useState(false);
  const [rebuildHint, setRebuildHint] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedRebuildUsers, setSelectedRebuildUsers] = useState([]);
  const [rebuildForAllUsers, setRebuildForAllUsers] = useState(true);
  const [rebuildForSelectedUsers, setRebuildForSelectedUsers] = useState(false);

  const normalizeStatus = (status) => {
    switch ((status || "").toLowerCase()) {
      case "approved":
      case "согласован":
        return "Согласован";
      case "rejected":
      case "declined":
      case "отклонено":
      case "не согласован":
        return "Отклонено";
      case "pending":
      case "на рассмотрении":
      default:
        return "На рассмотрении";
    }
  };

  const formatPersonShortName = (person) => {
    if (!person) return "-";
    const surname = person.surname || "";
    const firstInitial = person.name?.[0] ? `${person.name[0]}.` : "";
    return `${surname} ${firstInitial}`.trim() || "-";
  };

  // Загрузка списка согласований зарплат
  useEffect(() => {
    const loadDepartments = async () => {
      setIsLoading(true);
      setError(null);

      try {
        if (!userData?.id) {
          setDepartments([]);
          return;
        }

        const selectedYear = selectedMonth.getFullYear();
        const selectedMonthNumber = selectedMonth.getMonth() + 1;
        const approvals = await getSalaryApprovalsByApproverV2(userData.id);
        const filteredApprovals = approvals.filter(
          (approval) =>
            Number(approval?.year) === selectedYear &&
            Number(approval?.month) === selectedMonthNumber,
        );

        const mappedDepartments = filteredApprovals.map((approval) => ({
          id: approval.department?.id ?? approval.id,
          approvalId: approval.id,
          name: approval.department?.name || "Неизвестный отдел",
          initiator: formatPersonShortName(approval.initiator),
          approvers: (approval.steps || []).map((step, index) => ({
            role: `Согласующий ${index + 1}`,
            name: formatPersonShortName(step.approver),
            status: normalizeStatus(step.status),
            comment: step.comment || "",
            decidedAt: step.decided_at ? new Date(step.decided_at) : null,
          })),
          submissionDate: approval.created_at
            ? new Date(approval.created_at)
            : null,
          approvedAt: approval.approved_at
            ? new Date(approval.approved_at)
            : null,
          status: normalizeStatus(approval.status),
          year: approval.year,
          month: approval.month,
        }));

        setDepartments(mappedDepartments);
      } catch (err) {
        console.error("Ошибка при загрузке отделов:", err);
        setError(err.message || "Ошибка при загрузке данных");
        setDepartments([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadDepartments();
  }, [selectedMonth, userData?.id]);

  useEffect(() => {
    setRebuildHint(null);
  }, [selectedMonth]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const users = await getAllUsers();
        const mappedUsers = (Array.isArray(users) ? users : [])
          .filter((user) => user?.id)
          .sort((a, b) =>
            `${a?.surname || ""} ${a?.name || ""}`.localeCompare(
              `${b?.surname || ""} ${b?.name || ""}`,
              "ru",
            ),
          );
        setAllUsers(mappedUsers);
      } catch (err) {
        console.error("Ошибка при загрузке списка сотрудников:", err);
      }
    };

    loadUsers();
  }, []);

  useEffect(() => {
    if (!selectedMonth) return;
    const year = selectedMonth.getFullYear();
    const monthIndex = selectedMonth.getMonth();
    setPaymentsDateFrom(new Date(year, monthIndex, 1));
    setPaymentsDateTo(new Date(year, monthIndex + 1, 0));
  }, [selectedMonth]);

  const formatDateForApi = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleDepartmentClick = (department) => {
    setIsCreateMode(false);
    setSelectedDepartment(department);
  };

  const handleCreateApprovalClick = () => {
    setSelectedDepartment(null);
    setIsCreateMode(true);
  };

  const handleBack = () => {
    setIsCreateMode(false);
    setSelectedDepartment(null);
  };

  const handleRebuildPortalCache = async () => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth() + 1;
    const paymentsDateFromValue = formatDateForApi(paymentsDateFrom);
    const paymentsDateToValue = formatDateForApi(paymentsDateTo);
    if (!paymentsDateFromValue || !paymentsDateToValue) {
      setRebuildHint({
        type: "err",
        text: "Укажите период выплат (с и по).",
      });
      return;
    }
    if (rebuildForSelectedUsers && selectedRebuildUsers.length === 0) {
      setRebuildHint({
        type: "err",
        text: "Выберите хотя бы одного сотрудника для выборочного обновления.",
      });
      return;
    }
    setRebuildLoading(true);
    setRebuildHint(null);
    try {
      const result = await rebuildSalaryApprovalPortalCache({
        year,
        month,
        userIds: rebuildForSelectedUsers
          ? selectedRebuildUsers.map((user) => user.value)
          : undefined,
        paymentsDateFrom: paymentsDateFromValue,
        paymentsDateTo: paymentsDateToValue,
      });
      const msg =
        typeof result === "string"
          ? result
          : "Таблица расчёта зарплаты за выбранный месяц обновлена.";
      setRebuildHint({ type: "ok", text: msg });
    } catch (err) {
      console.error(err);
      setRebuildHint({
        type: "err",
        text: err?.message || "Не удалось обновить таблицу расчёта.",
      });
    } finally {
      setRebuildLoading(false);
    }
  };

  // Если выбран отдел или режим создания согласования, показываем детальную страницу
  if (selectedDepartment || isCreateMode) {
    return (
      <SalaryDetail
        department={isCreateMode ? null : selectedDepartment}
        onBack={handleBack}
        initialMonth={selectedMonth}
        lockMonthToInitial
        paymentsDateFrom={formatDateForApi(paymentsDateFrom)}
        paymentsDateTo={formatDateForApi(paymentsDateTo)}
      />
    );
  }

  // Иначе показываем список отделов
  return (
    <div className="financial-report">
      <div style={{ padding: "20px" }}>
        <div className="salary-rebuild-cache">
          <p className="salary-rebuild-cache__text">
            Посчитать зарплаты за выбранный месяц по всем сотрудникам и сформировать
            таблицу в портале — по ней можно посмотреть расчёт перед согласованием.
            Используются месяц и период выплат из полей ниже.
          </p>
          <div className="salary-rebuild-cache__actions">
            <label>Период выплат:</label>
            <div style={{ width: "170px" }}>
              <DatePicker
                selected={paymentsDateFrom}
                onChange={(date) => setPaymentsDateFrom(date)}
                dateFormat="dd.MM.yyyy"
                locale="ru"
                className="timesheet-datepicker"
                calendarClassName="custom-calendar"
                placeholderText="c"
              />
            </div>
            <span>—</span>
            <div style={{ width: "170px" }}>
              <DatePicker
                selected={paymentsDateTo}
                onChange={(date) => setPaymentsDateTo(date)}
                dateFormat="dd.MM.yyyy"
                locale="ru"
                className="timesheet-datepicker"
                calendarClassName="custom-calendar"
                placeholderText="по"
              />
            </div>
            <div className="salary-rebuild-cache__mode">
              <label className="salary-rebuild-cache__checkbox">
                <input
                  type="checkbox"
                  checked={rebuildForAllUsers}
                  onChange={() => {
                    setRebuildForAllUsers(true);
                    setRebuildForSelectedUsers(false);
                    setSelectedRebuildUsers([]);
                    setRebuildHint(null);
                  }}
                />
                <span>Обновить всех</span>
              </label>
              <label className="salary-rebuild-cache__checkbox">
                <input
                  type="checkbox"
                  checked={rebuildForSelectedUsers}
                  onChange={() => {
                    setRebuildForSelectedUsers(true);
                    setRebuildForAllUsers(false);
                    setRebuildHint(null);
                  }}
                />
                <span>Обновить выборочно</span>
              </label>
            </div>
            {rebuildForSelectedUsers && (
              <div className="salary-rebuild-cache__users-select">
                <Select
                  isMulti
                  value={selectedRebuildUsers}
                  onChange={(options) =>
                    setSelectedRebuildUsers(Array.isArray(options) ? options : [])
                  }
                  options={allUsers.map((user) => ({
                    value: user.id,
                    label: `${user.surname || ""} ${user.name || ""}`.trim(),
                  }))}
                  placeholder="Выберите сотрудников"
                  noOptionsMessage={() => "Пользователи не найдены"}
                />
              </div>
            )}
            <button
              type="button"
              className="salary-rebuild-cache__btn"
              disabled={rebuildLoading}
              onClick={handleRebuildPortalCache}
            >
              {rebuildLoading ? "Обновление…" : "Обновить таблицу расчёта"}
            </button>
            {rebuildHint && (
              <p
                className={`salary-rebuild-cache__hint salary-rebuild-cache__hint--${rebuildHint.type}`}
              >
                {rebuildHint.text}
              </p>
            )}
          </div>
        </div>
        <div className="salary-accountant-notice">
          Для бухгалтерии: сначала выберите месяц ниже, при необходимости нажмите
          «Обновить таблицу расчёта» выше — так в портале будут актуальные суммы.
          Затем можно создавать согласование и проверять расчёт до завершения
          согласования.
        </div>
        <h2>Заработная плата</h2>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            marginBottom: "20px",
          }}
        >
          <label>Месяц:</label>
          <div style={{ width: "200px" }}>
            <DatePicker
              selected={selectedMonth}
              onChange={(date) => setSelectedMonth(date)}
              dateFormat="MM.yyyy"
              showMonthYearPicker
              locale="ru"
              className="timesheet-datepicker"
              calendarClassName="custom-calendar month-year-calendar"
            />
          </div>
          <button
            type="button"
            onClick={handleCreateApprovalClick}
            style={{
              height: "40px",
              padding: "0 16px",
              borderRadius: "8px",
              // border: "1px solid #d1d5db",
              background: "#2A5E38",
              color: "#fff",
              cursor: "pointer",
              fontWeight: 500,
              fontSize: "14px",
              fontFamily: "Manrope",
            }}
          >
            Создать согласование
          </button>
        </div>
        {isLoading && (
          <div style={{ padding: "20px", textAlign: "center" }}>
            <p>Загрузка данных...</p>
          </div>
        )}
        {error && !isLoading && (
          <div style={{ padding: "20px", color: "red", textAlign: "center" }}>
            <p>Ошибка: {error}</p>
          </div>
        )}
        {!isLoading && (
          <SalaryTable
            selectedMonth={selectedMonth}
            departments={departments}
            onDepartmentClick={handleDepartmentClick}
          />
        )}
      </div>
    </div>
  );
}

export default Salary;
